/**
 * Native Service
 * Handles native mobile capabilities via Capacitor
 */
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { LocalNotifications } from '@capacitor/local-notifications';
import { Geolocation } from '@capacitor/geolocation';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { SocialLogin } from '@capgo/capacitor-social-login';


class NativeService {
    static isNative = Capacitor.isNativePlatform();

    // ==================== PUSH NOTIFICATIONS ====================

    /**
     * Initialize push notifications
     * Call this after user login
     */
    static async initPushNotifications() {
        if (!this.isNative) {
            console.log('Push notifications not available on web');
            return { success: false, reason: 'web_platform' };
        }

        try {
            // Request permission
            const permissionStatus = await PushNotifications.requestPermissions();

            if (permissionStatus.receive !== 'granted') {
                console.log('Push notification permission denied');
                return { success: false, reason: 'permission_denied' };
            }

            // Register for push notifications
            await PushNotifications.register();

            // Listen for registration success
            PushNotifications.addListener('registration', async (token) => {
                console.log('Push notification registration success:', token.value);

                // Send token to backend using fetch directly
                try {
                    const API_BASE = 'https://calendario.xerp.es/api';
                    const authToken = localStorage.getItem('token');

                    const response = await fetch(`${API_BASE}/notifications/register-token`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': authToken ? `Bearer ${authToken}` : ''
                        },
                        body: JSON.stringify({
                            token: token.value,
                            platform: Capacitor.getPlatform(),
                            deviceInfo: navigator.userAgent
                        })
                    });

                    if (response.ok) {
                        console.log('Token registered with backend successfully');
                        // Save token for logout unregistration (RGPD compliance)
                        localStorage.setItem('fcm_token', token.value);
                    } else {

                        console.error('Failed to register token:', await response.text());
                    }
                } catch (error) {
                    console.error('Error registering token with backend:', error);
                }
            });


            // Listen for registration error
            PushNotifications.addListener('registrationError', (error) => {
                console.error('Push notification registration error:', error);
            });

            // Listen for notifications received
            PushNotifications.addListener('pushNotificationReceived', (notification) => {
                console.log('Push notification received:', notification);
                // You can show a toast, update UI, etc.
                this._handleNotificationReceived(notification);
            });

            // Listen for notification action (tap)
            PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
                console.log('Push notification action performed:', action);
                this._handleNotificationAction(action);
            });

            return { success: true };
        } catch (error) {
            console.error('Error initializing push notifications:', error);
            return { success: false, reason: error.message };
        }
    }

    /**
     * Handle received notification (while app is in foreground)
     */
    static _handleNotificationReceived(notification) {
        // Dispatch custom event so components can react
        window.dispatchEvent(new CustomEvent('pushNotificationReceived', {
            detail: notification
        }));
    }

    /**
     * Handle notification action (user tapped notification)
     */
    static _handleNotificationAction(action) {
        // Navigate based on notification data
        const data = action.notification?.data;

        if (data?.route) {
            // Navigate to specified route
            window.location.href = data.route;
        }

        window.dispatchEvent(new CustomEvent('pushNotificationAction', {
            detail: action
        }));
    }

    /**
     * Unregister device token (call on logout)
     */
    static async unregisterPushNotifications() {
        if (!this.isNative) return;

        try {
            await PushNotifications.removeAllListeners();
            // Token unregistration is handled on backend when user logs out
            console.log('Push notifications unregistered');
        } catch (error) {
            console.error('Error unregistering push notifications:', error);
        }
    }

    // ==================== LOCAL NOTIFICATIONS ====================

    /**
     * Schedule a local notification
     */
    static async scheduleNotification({ title, body, id, schedule, extra = null }) {
        if (!this.isNative) {
            console.log('Local notifications not available on web');
            // Fallback to browser notification?
            if ('Notification' in window && Notification.permission === 'granted') {
                new Notification(title, { body });
            }
            return { success: true, simulated: true };
        }

        try {
            // Check permissions
            const permission = await LocalNotifications.checkPermissions();
            if (permission.display !== 'granted') {
                const requestHandler = await LocalNotifications.requestPermissions();
                if (requestHandler.display !== 'granted') {
                    return { success: false, reason: 'permission_denied' };
                }
            }

            await LocalNotifications.schedule({
                notifications: [
                    {
                        title,
                        body,
                        id: id || Math.floor(Math.random() * 100000),
                        schedule: schedule || { at: new Date(Date.now() + 1000 * 5) }, // Default 5s
                        sound: null,
                        attachments: null,
                        actionTypeId: '',
                        extra
                    }
                ]
            });

            return { success: true };
        } catch (error) {
            console.error('Error scheduling local notification:', error);
            return { success: false, reason: error.message };
        }
    }

    /**
     * Cancel specific notifications
     */
    static async cancelNotifications(ids) {
        if (!this.isNative) return;
        try {
            await LocalNotifications.cancel({ notifications: ids.map(id => ({ id })) });
        } catch (error) {
            console.error('Error canceling notifications:', error);
        }
    }


    // ==================== GEOLOCATION (GPS) ====================

    /**
     * Get current GPS position
     */
    static async getCurrentPosition() {
        try {
            // Check permission first
            const permission = await Geolocation.checkPermissions();

            if (permission.location !== 'granted') {
                const requestResult = await Geolocation.requestPermissions();
                if (requestResult.location !== 'granted') {
                    return { success: false, reason: 'permission_denied' };
                }
            }

            const position = await Geolocation.getCurrentPosition({
                enableHighAccuracy: true,
                timeout: 10000,
                maximumAge: 0
            });

            return {
                success: true,
                latitude: position.coords.latitude,
                longitude: position.coords.longitude,
                accuracy: position.coords.accuracy,
                timestamp: position.timestamp
            };
        } catch (error) {
            console.error('Error getting GPS position:', error);
            return { success: false, reason: error.message };
        }
    }

    /**
     * Watch position changes (for real-time tracking)
     */
    static async watchPosition(callback) {
        try {
            const permission = await Geolocation.checkPermissions();

            if (permission.location !== 'granted') {
                const requestResult = await Geolocation.requestPermissions();
                if (requestResult.location !== 'granted') {
                    return { success: false, reason: 'permission_denied' };
                }
            }

            const watchId = await Geolocation.watchPosition({
                enableHighAccuracy: true,
                timeout: 10000,
                maximumAge: 0
            }, (position, error) => {
                if (error) {
                    callback(null, error);
                    return;
                }
                callback({
                    latitude: position.coords.latitude,
                    longitude: position.coords.longitude,
                    accuracy: position.coords.accuracy,
                    timestamp: position.timestamp
                }, null);
            });

            return { success: true, watchId };
        } catch (error) {
            console.error('Error watching position:', error);
            return { success: false, reason: error.message };
        }
    }

    /**
     * Stop watching position
     */
    static async clearWatch(watchId) {
        try {
            await Geolocation.clearWatch({ id: watchId });
            return { success: true };
        } catch (error) {
            console.error('Error clearing watch:', error);
            return { success: false, reason: error.message };
        }
    }

    // ==================== CAMERA ====================

    /**
     * Take a photo with camera
     */
    static async takePhoto(options = {}) {
        try {
            // Check permission
            const permission = await Camera.checkPermissions();

            if (permission.camera !== 'granted') {
                const requestResult = await Camera.requestPermissions({ permissions: ['camera'] });
                if (requestResult.camera !== 'granted') {
                    return { success: false, reason: 'permission_denied' };
                }
            }

            const image = await Camera.getPhoto({
                quality: options.quality || 90,
                allowEditing: options.allowEditing || false,
                resultType: CameraResultType.Base64,
                source: CameraSource.Camera,
                saveToGallery: options.saveToGallery || false,
                width: options.width || 1200,
                height: options.height || 1200,
                correctOrientation: true
            });

            return {
                success: true,
                base64: image.base64String,
                format: image.format,
                dataUrl: `data:image/${image.format};base64,${image.base64String}`
            };
        } catch (error) {
            if (error.message?.includes('User cancelled')) {
                return { success: false, reason: 'cancelled' };
            }
            console.error('Error taking photo:', error);
            return { success: false, reason: error.message };
        }
    }

    /**
     * Pick photo from gallery
     */
    static async pickPhoto(options = {}) {
        try {
            // Check permission
            const permission = await Camera.checkPermissions();

            if (permission.photos !== 'granted') {
                const requestResult = await Camera.requestPermissions({ permissions: ['photos'] });
                if (requestResult.photos !== 'granted') {
                    return { success: false, reason: 'permission_denied' };
                }
            }

            const image = await Camera.getPhoto({
                quality: options.quality || 90,
                allowEditing: options.allowEditing || false,
                resultType: CameraResultType.Base64,
                source: CameraSource.Photos,
                width: options.width || 1200,
                height: options.height || 1200,
                correctOrientation: true
            });

            return {
                success: true,
                base64: image.base64String,
                format: image.format,
                dataUrl: `data:image/${image.format};base64,${image.base64String}`
            };
        } catch (error) {
            if (error.message?.includes('User cancelled')) {
                return { success: false, reason: 'cancelled' };
            }
            console.error('Error picking photo:', error);
            return { success: false, reason: error.message };
        }
    }

    /**
     * Choose photo from camera or gallery (shows prompt)
     */
    static async choosePhoto(options = {}) {
        try {
            const image = await Camera.getPhoto({
                quality: options.quality || 90,
                allowEditing: options.allowEditing || false,
                resultType: CameraResultType.Base64,
                source: CameraSource.Prompt, // Shows camera/gallery prompt
                width: options.width || 1200,
                height: options.height || 1200,
                correctOrientation: true,
                promptLabelHeader: options.promptHeader || 'Seleccionar foto',
                promptLabelCancel: options.promptCancel || 'Cancelar',
                promptLabelPhoto: options.promptPhoto || 'Galería',
                promptLabelPicture: options.promptPicture || 'Cámara'
            });

            return {
                success: true,
                base64: image.base64String,
                format: image.format,
                dataUrl: `data:image/${image.format};base64,${image.base64String}`
            };
        } catch (error) {
            if (error.message?.includes('User cancelled')) {
                return { success: false, reason: 'cancelled' };
            }
            console.error('Error choosing photo:', error);
            return { success: false, reason: error.message };
        }
    }

    // ==================== GOOGLE AUTH ====================

    /**
     * Initialize Google Auth (call once on app start)
     */
    static async initGoogleAuth() {
        if (!this.isNative) {
            console.log('Native Google Auth not available on web');
            return { success: false, reason: 'web_platform' };
        }

        try {
            await SocialLogin.initialize({
                google: {
                    webClientId: '536523793878-7k7s9i9qas6uvivvh20v25tac7saimvv.apps.googleusercontent.com'
                }
            });
            console.log('Google Auth initialized');
            return { success: true };
        } catch (error) {
            console.error('Error initializing Google Auth:', error);
            return { success: false, reason: error.message };
        }
    }

    /**
     * Sign in with Google (native)
     * Returns user data and idToken for backend verification
     */
    static async signInWithGoogle() {
        if (!this.isNative) {
            console.log('Native Google Auth not available on web');
            return { success: false, reason: 'web_platform' };
        }

        try {
            const result = await SocialLogin.login({
                provider: 'google',
                options: {}
            });

            console.log('Google Sign-In raw result:', JSON.stringify(result));

            // Handle different response structures from the plugin
            const data = result?.result || result;

            if (data) {
                // Try to extract idToken from various possible locations
                const idToken = data.idToken ||
                    data.authentication?.idToken ||
                    data.serverAuthCode ||
                    data.accessToken?.token;

                // Try to extract profile from various possible locations
                const profile = data.profile || data.user || data;

                console.log('Extracted idToken:', idToken ? 'present' : 'missing');
                console.log('Extracted profile:', JSON.stringify(profile));

                if (idToken) {
                    return {
                        success: true,
                        idToken: idToken,
                        user: {
                            email: profile?.email,
                            name: profile?.name || profile?.displayName || profile?.givenName,
                            picture: profile?.picture || profile?.imageUrl || profile?.photoUrl,
                            googleId: profile?.id || profile?.sub
                        }
                    };
                }

                // If no idToken but we have profile data, try using accessToken
                if (profile?.email) {
                    const accessToken = data.accessToken?.token || data.accessToken;
                    console.log('No idToken, trying accessToken:', accessToken ? 'present' : 'missing');

                    return {
                        success: true,
                        idToken: accessToken, // Backend will try to verify this
                        user: {
                            email: profile.email,
                            name: profile.name || profile.displayName,
                            picture: profile.picture || profile.imageUrl,
                            googleId: profile.id
                        }
                    };
                }
            }

            console.log('No valid result from Google Sign-In');
            return { success: false, reason: 'no_result' };
        } catch (error) {
            console.error('Error signing in with Google:', error);
            return { success: false, reason: error.message };
        }
    }

    /**
     * Sign out from Google
     */
    static async signOutGoogle() {
        if (!this.isNative) return { success: true };

        try {
            await SocialLogin.logout({ provider: 'google' });
            return { success: true };
        } catch (error) {
            console.error('Error signing out from Google:', error);
            return { success: false, reason: error.message };
        }
    }

    // ==================== UTILITY ====================

    /**
     * Check if running on native platform
     */
    static isNativePlatform() {
        return this.isNative;
    }

    /**
     * Get current platform
     */
    static getPlatform() {
        return Capacitor.getPlatform();
    }
}

export default NativeService;
